import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { 
  EyeIcon, 
  EyeOffIcon, 
  ShieldCheckIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  XIcon,
  InfoIcon,
  TrashIcon,
  AlertTriangleIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SecuritySettings = () => {
  const { user } = useAuth();
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [passwordChangedAt, setPasswordChangedAt] = useState(null);
  
  // Delete account states
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    confirmText: '',
    password: '',
    loading: false,
    errors: { confirmText: '', password: '' }
  });
  
  // Final confirmation and success modal
  const [finalDeleteModal, setFinalDeleteModal] = useState({
    show: false,
    step: 'confirm', // 'confirm' or 'success'
    countdown: 3
  });

  const [companyDeleteWarning, setCompanyDeleteWarning] = useState({
    show: false,
    password: '',
    error: '',
    loading: false
  });

  const [isLastUser, setIsLastUser] = useState(false);

  useEffect(() => {
    if (user?.user?.password_changed_at) {
      setPasswordChangedAt(user.user.password_changed_at);
    }
    
    // Check if this is the last user in company
    checkLastUser();
  }, [user]);

  const checkLastUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/company/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const currentUserCount = response.data.current_user_count || 0;
      setIsLastUser(currentUserCount === 1);
    } catch (error) {
      console.error('Error checking user count:', error);
      setIsLastUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Bugün';
    } else if (diffDays === 1) {
      return 'Dün';
    } else if (diffDays < 30) {
      return `${diffDays} gün önce`;
    } else {
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {
      length: false,
      uppercase: false,
      lowercase: false,
      special: false
    },
    isValid: false
  });

  const validatePasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    const isValid = Object.values(checks).every(Boolean);
    
    return { score, checks, isValid };
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'new_password') {
      setPasswordStrength(validatePasswordStrength(value));
    }
  };

  const getStrengthLabel = (passwordStrength) => {
    if (passwordStrength.isValid) {
      return { label: 'Güçlü', color: 'text-green-500', bgColor: 'bg-green-100' };
    } else {
      return { label: 'Zayıf', color: 'text-red-500', bgColor: 'bg-red-100' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!passwordData.new_password) {
      toast.error('Yeni şifrenizi girin');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    
    if (!passwordStrength.isValid) {
      toast.error('Şifre tüm güvenlik kurallarını karşılamalıdır');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          new_password: passwordData.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Şifre başarıyla değiştirildi');
      
      // Update password changed date
      setPasswordChangedAt(new Date().toISOString());
      
      // Clear form
      setPasswordData({
        new_password: '',
        confirm_password: ''
      });
      setPasswordStrength({
        score: 0,
        checks: {
          length: false,
          uppercase: false,
          lowercase: false,
          special: false
        },
        isValid: false
      });

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDeleteAccount = async () => {
    // Clear previous errors
    let hasErrors = false;
    const newErrors = { confirmText: '', password: '' };

    // Validation
    if (deleteModal.confirmText.trim().toLowerCase() !== "hesabımı silmene onay veriyorum") {
      newErrors.confirmText = 'Onay metni doğru yazılmalı: "hesabımı silmene onay veriyorum"';
      hasErrors = true;
    }

    if (!deleteModal.password.trim()) {
      newErrors.password = 'Şifrenizi girmeniz gerekiyor';
      hasErrors = true;
    }

    if (hasErrors) {
      setDeleteModal(prev => ({ ...prev, errors: newErrors }));
      return;
    }

    // VERIFY PASSWORD FIRST before proceeding
    setDeleteModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      // Verify password using login endpoint with current user email
      await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: user.user.email,
        password: deleteModal.password.trim()
      });
      
      // Password is correct, proceed with deletion flow
      setDeleteModal(prev => ({ ...prev, loading: false }));
      
      // If last user, show company deletion warning
      if (isLastUser) {
        setCompanyDeleteWarning({ 
          show: true, 
          password: '', 
          error: '',
          loading: false 
        });
        return;
      }

      // Otherwise show final confirmation modal
      setFinalDeleteModal({ show: true, step: 'confirm', countdown: 3 });
      
    } catch (error) {
      console.error('Password verification failed:', error);
      const errorMsg = error.response?.data?.detail || 'Şifre doğrulaması başarısız';
      
      setDeleteModal(prev => ({ 
        ...prev, 
        loading: false,
        errors: { ...prev.errors, password: 'Girmiş olduğunuz şifre hatalıdır. Lütfen doğru şifrenizi giriniz.' }
      }));
    }
  };

  const handleFinalConfirmation = async () => {
    console.log('🔴 Starting account deletion...');
    setFinalDeleteModal(prev => ({ ...prev, step: 'deleting' }));

    try {
      const token = localStorage.getItem('token');
      console.log('Token:', token ? 'exists' : 'missing');
      console.log('Request data:', {
        confirmation_text: deleteModal.confirmText.trim(),
        password: '***' // Don't log actual password
      });
      
      const response = await axios.delete(`${BACKEND_URL}/api/auth/delete-my-account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          confirmation_text: deleteModal.confirmText.trim(),
          password: deleteModal.password.trim()
        }
      });

      console.log('✅ Delete response:', response.data);

      // Close initial delete modal
      setDeleteModal({
        show: false,
        confirmText: '',
        password: '',
        loading: false,
        errors: { confirmText: '', password: '' }
      });

      // Show success message
      setFinalDeleteModal({ show: true, step: 'success', countdown: 3 });
      
      // Start countdown and logout after 3 seconds
      let timeLeft = 3;
      const countdownInterval = setInterval(() => {
        timeLeft -= 1;
        setFinalDeleteModal(prev => ({ ...prev, countdown: timeLeft }));
        
        if (timeLeft <= 0) {
          clearInterval(countdownInterval);
          
          // Clear storage and redirect
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Delete error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Hesap silinirken hata oluştu';
      
      // Close final modal and reopen delete modal with error
      setFinalDeleteModal({ show: false, step: 'confirm', countdown: 3 });
      
      // Check if it's a field-specific error
      if (errorMsg.includes('Şifre hatalı') || errorMsg.includes('password')) {
        setDeleteModal(prev => ({ 
          ...prev, 
          show: true,
          loading: false,
          errors: { ...prev.errors, password: 'Şifre hatalı' }
        }));
      } else if (errorMsg.includes('onay') || errorMsg.includes('confirmation')) {
        setDeleteModal(prev => ({ 
          ...prev,
          show: true, 
          loading: false,
          errors: { ...prev.errors, confirmText: errorMsg }
        }));
      } else {
        toast.error(errorMsg);
        setDeleteModal(prev => ({ ...prev, show: true, loading: false }));
      }
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      show: false,
      confirmText: '',
      password: '',
      loading: false,
      errors: { confirmText: '', password: '' }
    });
  };

  const strengthInfo = getStrengthLabel(passwordStrength);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Güvenlik Ayarları</h3>
            <p className="text-sm text-gray-500">Hesap güvenliğinizi yönetin</p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <Card className="p-4 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <KeyIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{user?.user?.full_name}</p>
              <p className="text-sm text-gray-500">{user?.user?.email}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Son şifre değişikliği</p>
            <p className="text-sm font-medium text-gray-700">{formatDate(passwordChangedAt)}</p>
          </div>
        </div>
      </Card>

      {/* Password Change Form */}
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center space-x-2 mb-4">
            <KeyIcon className="w-5 h-5 text-gray-600" />
            <h4 className="text-md font-semibold text-gray-900">Şifre Değiştir</h4>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="new_password">Yeni Şifre</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                placeholder="Yeni şifrenizi girin"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {passwordData.new_password && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Şifre Gücü:</span>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${strengthInfo.bgColor} ${strengthInfo.color}`}>
                    {strengthInfo.label}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.isValid ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${passwordStrength.isValid ? 100 : (passwordStrength.score / 4) * 80}%` }}
                  />
                </div>

                {/* Requirements Checklist */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className={`flex items-center space-x-2 ${passwordStrength.checks.length ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordStrength.checks.length ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                    <span>En az 8 karakter</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${passwordStrength.checks.uppercase ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordStrength.checks.uppercase ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                    <span>En az 1 büyük harf (A-Z)</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${passwordStrength.checks.lowercase ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordStrength.checks.lowercase ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                    <span>En az 1 küçük harf (a-z)</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${passwordStrength.checks.special ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordStrength.checks.special ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                    <span>En az 1 özel karakter (!@#$)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Yeni Şifre Tekrarı</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                placeholder="Yeni şifrenizi tekrar girin"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password Match Indicator */}
            {passwordData.confirm_password && (
              <div className={`flex items-center space-x-2 text-sm ${
                passwordData.new_password === passwordData.confirm_password ? 'text-green-600' : 'text-red-600'
              }`}>
                {passwordData.new_password === passwordData.confirm_password ? 
                  <CheckCircleIcon className="w-4 h-4" /> : 
                  <XCircleIcon className="w-4 h-4" />
                }
                <span>
                  {passwordData.new_password === passwordData.confirm_password ? 
                    'Şifreler eşleşiyor' : 
                    'Şifreler eşleşmiyor'
                  }
                </span>
              </div>
            )}
          </div>

          {/* Security Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <InfoIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Güvenlik İpuçları:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Şifrenizi düzenli olarak değiştirin</li>
                  <li>• Başka hesaplarda kullandığınız şifreyi kullanmayın</li>
                  <li>• Şifrenizi kimseyle paylaşmayın</li>
                  <li>• Güçlü ve benzersiz şifreler oluşturun</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !passwordStrength.isValid || passwordData.new_password !== passwordData.confirm_password}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Güncelleniyor...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <ShieldCheckIcon className="w-4 h-4" />
                <span>Şifreyi Değiştir</span>
              </div>
            )}
          </Button>
        </form>
      </Card>

      {/* Delete Account Section - Hide for Master Template users */}
      {!user?.user?.is_master_template_user && (
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrashIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-red-900">Tehlikeli Bölge</h4>
              <p className="text-sm text-red-700">Bu işlemler geri alınamaz</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-red-200">
            <div className="flex items-start space-x-4">
              <AlertTriangleIcon className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 mb-2">Hesabımı Kalıcı Olarak Sil</h5>
                <p className="text-sm text-gray-600 mb-4">
                  Hesabınızı sildiğinizde, tüm verileriniz kalıcı olarak silinir ve bu işlem geri alınamaz. 
                  Eğer şirketinizde başka kullanıcılar varsa, önce onları silmeniz gerekir.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start space-x-2">
                    <InfoIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800">
                      <p className="font-medium mb-1">Hesap silme kuralları:</p>
                      <ul className="space-y-1">
                        <li>• Normal kullanıcılar kendi hesabını silebilir</li>
                        <li>• Admin kullanıcılar, altında kullanıcı varsa hesabını silemez</li>
                        <li>• Son kalan kullanıcı silinirse şirket de silinir</li>
                        <li>• Tüm veriler kalıcı olarak silinir</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setDeleteModal(prev => ({ ...prev, show: true }))}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Hesabımı Sil
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      
      {/* Master Template Protection Notice */}
      {user?.user?.is_master_template_user && (
        <Card className="p-6 border-blue-200 bg-blue-50">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-blue-900">Korumalı Master Hesap</h4>
              <p className="text-sm text-blue-700">Bu hesap sistem tarafından korunmaktadır</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <div className="flex items-start space-x-4">
              <InfoIcon className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 mb-2">Master Template Hesabı</h5>
                <p className="text-sm text-gray-600 mb-4">
                  Bu hesap, paket özelliklerinin şablon olarak kullanıldığı bir master hesaptır. 
                  Sistem bütünlüğü için bu hesap silinemez ve süresiz kullanımdadır.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-800">
                      <p className="font-medium mb-1">Master hesap özellikleri:</p>
                      <ul className="space-y-1">
                        <li>• Süresiz kullanım hakkı</li>
                        <li>• Hesap silinemez</li>
                        <li>• Şirket silinemez</li>
                        <li>• Tüm özelliklere erişim</li>
                        <li>• Yeni kayıtlar için şablon olarak kullanılır</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Account Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-8 h-8 text-red-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Hesabımı Kalıcı Olarak Sil
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                Bu işlem <strong>geri alınamaz</strong>. Tüm verileriniz kalıcı olarak silinecek.
              </p>
            </div>

            <div className="space-y-4">
              {/* Confirmation Text */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Onaylamak için aşağıdaki metni yazın:
                </Label>
                <p className="text-xs text-gray-500 mb-2">
                  <code className="bg-gray-100 px-2 py-1 rounded">"hesabımı silmene onay veriyorum"</code>
                </p>
                <Input
                  type="text"
                  value={deleteModal.confirmText}
                  onChange={(e) => setDeleteModal(prev => ({ 
                    ...prev, 
                    confirmText: e.target.value,
                    errors: { ...prev.errors, confirmText: '' }
                  }))}
                  placeholder="hesabımı silmene onay veriyorum"
                  className={`text-sm ${deleteModal.errors.confirmText ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {deleteModal.errors.confirmText && (
                  <p className="mt-1 text-xs text-red-600">
                    {deleteModal.errors.confirmText}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Şifrenizi girin
                </Label>
                <Input
                  type="password"
                  value={deleteModal.password}
                  onChange={(e) => setDeleteModal(prev => ({ 
                    ...prev, 
                    password: e.target.value,
                    errors: { ...prev.errors, password: '' }
                  }))}
                  placeholder="Mevcut şifreniz"
                  className={`text-sm ${deleteModal.errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {deleteModal.errors.password && (
                  <p className="mt-1 text-xs text-red-600">
                    {deleteModal.errors.password}
                  </p>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={closeDeleteModal}
                disabled={deleteModal.loading}
              >
                İptal
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteModal.loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteModal.loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Siliniyor...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <TrashIcon className="w-4 h-4" />
                    <span>Evet, Sil</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Company Deletion Warning Modal - FOR LAST USER */}
      {companyDeleteWarning.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6">
            <div className="space-y-6">
              {/* Warning Header */}
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangleIcon className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-red-600">KRİTİK UYARI!</h3>
                  <p className="text-gray-600 mt-1">Şirket hesabı da silinecek</p>
                </div>
              </div>

              {/* Warning Content */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 space-y-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangleIcon className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                  <div className="space-y-3">
                    <p className="font-semibold text-red-900 text-lg">
                      Hesabınızı silme işlemi ile Süper Admin olduğunuz bu şirket hesabı da otomatik olarak silinecektir.
                    </p>
                    
                    <div className="space-y-2 text-red-800">
                      <p className="font-medium">📋 Bu işlem sonucunda:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Şirket hesabınız <strong>kalıcı olarak</strong> sistemimizden kaldırılacaktır</li>
                        <li>Tüm kullanıcı kayıtlarınız <strong>kalıcı olarak silinecektir</strong></li>
                        <li>Aboneliğiniz <strong>otomatik olarak sonlandırılacaktır</strong></li>
                        <li>Tüm avans talepleri ve raporlarınız <strong>silinecektir</strong></li>
                        <li>Bu işlem <strong>GERİ ALINAMAZ</strong></li>
                      </ul>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-4">
                      <p className="text-sm font-medium text-yellow-900">
                        ⚠️ Bu hesaba daha sonra <strong>hiçbir şekilde erişemeyeceksiniz</strong> ve 
                        verileriniz <strong>geri getirilemeyecektir</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Question */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-center font-semibold text-gray-900 text-lg mb-4">
                  Bu koşulları kabul ediyor musunuz?
                </p>

                {/* Password Verification */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Devam etmek için şifrenizi girin:
                  </label>
                  <Input
                    type="password"
                    value={companyDeleteWarning.password}
                    onChange={(e) => setCompanyDeleteWarning(prev => ({ 
                      ...prev, 
                      password: e.target.value,
                      error: ''
                    }))}
                    placeholder="Şifrenizi girin"
                    className="w-full"
                    disabled={companyDeleteWarning.loading}
                  />
                  {companyDeleteWarning.error && (
                    <p className="text-sm text-red-600 flex items-center">
                      <XIcon className="w-4 h-4 mr-1" />
                      {companyDeleteWarning.error}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setCompanyDeleteWarning({ show: false, password: '', error: '', loading: false });
                      setDeleteModal({ show: false, confirmText: '', password: '', loading: false, errors: {} });
                    }}
                    disabled={companyDeleteWarning.loading}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    onClick={async () => {
                      if (!companyDeleteWarning.password) {
                        setCompanyDeleteWarning(prev => ({ 
                          ...prev, 
                          error: 'Lütfen şifrenizi girin' 
                        }));
                        return;
                      }

                      // Verify password
                      setCompanyDeleteWarning(prev => ({ ...prev, loading: true, error: '' }));
                      
                      try {
                        // Verify password using login endpoint
                        await axios.post(`${BACKEND_URL}/api/auth/login`, {
                          email: user.user.email,
                          password: companyDeleteWarning.password
                        });

                        // Password correct, proceed with final deletion
                        setCompanyDeleteWarning({ show: false, password: '', error: '', loading: false });
                        setFinalDeleteModal({ show: true, step: 'confirm', countdown: 3 });
                      } catch (error) {
                        setCompanyDeleteWarning(prev => ({ 
                          ...prev, 
                          loading: false,
                          error: 'Girmiş olduğunuz şifre hatalıdır. Lütfen doğru şifrenizi giriniz.' 
                        }));
                      }
                    }}
                    disabled={companyDeleteWarning.loading}
                  >
                    {companyDeleteWarning.loading ? 'Doğrulanıyor...' : 'Evet, Onaylıyorum'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Final Confirmation/Success Modal */}
      {finalDeleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            
            {/* Final Confirmation Step */}
            {finalDeleteModal.step === 'confirm' && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangleIcon className="w-8 h-8 text-red-600" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Emin misiniz?
                </h3>
                
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Hesabınızı <strong>kalıcı olarak silmek</strong> üzeresiniz. 
                  Bu işlem <strong>geri alınamaz</strong> ve tüm verileriniz tamamen silinecek.
                  <br/><br/>
                  Gerçekten devam etmek istiyor musunuz?
                </p>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setFinalDeleteModal({ show: false, step: 'confirm', countdown: 3 })}
                  >
                    Hayır, İptal Et
                  </Button>
                  <Button
                    onClick={handleFinalConfirmation}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Evet, Sil
                  </Button>
                </div>
              </div>
            )}

            {/* Deleting Step */}
            {finalDeleteModal.step === 'deleting' && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Hesap Siliniyor...
                </h3>
                
                <p className="text-gray-600 text-sm">
                  Lütfen bekleyin, hesabınız siliniyor.
                </p>
              </div>
            )}

            {/* Success Step */}
            {finalDeleteModal.step === 'success' && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-green-600" />
                </div>
                
                <h3 className="text-xl font-bold text-green-900 mb-2">
                  Hesap Silinmiştir
                </h3>
                
                <p className="text-gray-600 text-sm mb-4">
                  Hesabınız başarıyla silindi. Tüm verileriniz kalıcı olarak temizlendi.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>{finalDeleteModal.countdown}</strong> saniye sonra ana sayfaya yönlendirileceksiniz...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;